import { createAction, createReducer } from '@reduxjs/toolkit';
import { LOCATION_CHANGE, LocationChangeAction } from 'connected-react-router';
import { denormalize } from 'normalizr';
import { matchPath } from 'react-router-dom';
import { LOGOUT_API_SUCCESS } from '../../global/logout/constants';
import { RESET_SESSION } from '../../global/session/constants';
import { PROFILE_URL } from '../../global/urls';
import { ApiSuccessResultActionPayload, makeApiRequestSlice } from '../../helpers/apiRequestSlice';
import { ShopEntity, ShopSchema } from '../../schemas';
import { NormalizedResponse } from '../../types';
import { LOGIN_API_SUCCESS } from '../Login/constants';
import { SELECT_SHOP_API_SUCCESS } from '../ShopSelector/constants';

// fetch

type BrandProfileFetchPayload = Record<string, never>;

interface BrandProfileSuccessPayload {
  request: BrandProfileFetchPayload;
  response: NormalizedResponse & {
    result: {
      shop: number;
    };
  };
}

export const fetchBrandProfileSlice = makeApiRequestSlice<
  BrandProfileFetchPayload,
  BrandProfileSuccessPayload
>({
  name: 'brandProfile',
});

// save

export type SaveBrandProfileRequestPayload = { shopId: number | null };

export type SaveBrandProfileResult = {
  shop: number;
};

export type SaveBrandProfileSuccessPayload = ApiSuccessResultActionPayload<
  SaveBrandProfileRequestPayload,
  SaveBrandProfileResult
>;

export const saveBrandProfileSlice = makeApiRequestSlice<
  SaveBrandProfileRequestPayload,
  SaveBrandProfileSuccessPayload
>({ name: 'brandProfile/save' });

// state

export interface BrandProfileState {
  logoUrl: string | null;
  shopId: number | null;
}

const initialState: BrandProfileState = {
  logoUrl: null,
  shopId: null,
};

const resetState = () => initialState;

export const setLogoUrl = createAction<string>('brandProfile/setLogoUrl');

export const brandProfilePageReducer = createReducer(initialState, (builder) => {
  builder.addCase(fetchBrandProfileSlice.actions.success, (draft, action) => {
    const { response } = action.payload;
    draft.shopId = response.result.shop;
    const { entities } = response;
    const shop: ShopEntity = denormalize(draft.shopId, ShopSchema, entities);
    draft.logoUrl = shop.logoUrl ? process.env.REACT_APP_DJANGO_BASE_URL + shop.logoUrl : '';
  });
  builder.addCase(setLogoUrl, (draft, action) => {
    draft.logoUrl = action.payload;
  });
  builder.addCase(LOCATION_CHANGE, (draft, action) => {
    const { location } = (action as LocationChangeAction).payload;
    const locationMatch = matchPath(location.pathname, {
      path: PROFILE_URL,
      exact: true,
    });
    return locationMatch ? initialState : draft;
  });
  builder.addCase(LOGIN_API_SUCCESS, resetState);
  builder.addCase(LOGOUT_API_SUCCESS, resetState);
  builder.addCase(RESET_SESSION, resetState);
  builder.addCase(SELECT_SHOP_API_SUCCESS, resetState);
});
